import { useMemo } from "react";

export const isErEncounter = (chat) => chat?.contextVariables?.["local:encounterType"] === "er";

export const canUseReassessmentPhase = (chat) => !!chat?.contextVariables?.["local:erHPI_transcript"];

// Disposition phases that require HPI to be recorded before they can be used
export const DISPOSITION_PHASES = ["erDischarge", "erAdmit", "erTransfer", "erHandoff"];

export const DISPO_VARIABLE_TO_LABEL = {
  erDischarge: "Discharge",
  erAdmit: "Admit",
  erTransfer: "Transfer",
  erHandoff: "Handoff",
};

export const canUseDispositionPhase = (chat) => !!chat?.contextVariables?.["local:erHPI_transcript"];

export const canUseProcedurePhase = (chat) => !!chat?.contextVariables?.["local:erHPI_transcript"];

export const getEncounterSections = (encounterType) => encounterType?.sections || [];

export const getSelectablePhases = (phases, chat) => {
  if (!Array.isArray(phases)) return [];

  return phases.filter((phase) => {
    // Filter out erReassessment if HPI not recorded
    if (phase.variable === "erReassessment" && !canUseReassessmentPhase(chat)) {
      return false;
    }

    // Filter out disposition phases if HPI not recorded
    if (DISPOSITION_PHASES.includes(phase.variable) && !canUseDispositionPhase(chat)) {
      return false;
    }

    if (phase.variable === "erProcedure" && !canUseProcedurePhase(chat)) {
      return false;
    }

    return true;
  });
};

export const getHighestReassessmentIndex = (chat) => {
  if (!chat?.contextVariables) return 0;

  let maxIndex = 0;

  if (chat.contextVariables["local:erReassessment_transcript"]) {
    maxIndex = 1;
  }

  for (let i = 1; i < 50; i++) {
    if (chat.contextVariables[`local:erReassessment_${i}_transcript`]) {
      maxIndex = Math.max(maxIndex, i);
    }
  }

  return maxIndex;
};

export const getNextReassessmentIndex = (chat) => {
  if (!chat?.contextVariables) return 1;

  let finalizedReassessments = parseInt(chat.contextVariables["local:totalReassessments"], 10);
  if (Number.isNaN(finalizedReassessments) || finalizedReassessments < 0) finalizedReassessments = 0;

  const baseTranscript = chat.contextVariables["local:erReassessment_transcript"];
  const baseCompleted =
    chat.contextVariables["local:erReassessment_on"] || chat.contextVariables["local:erReassessment_transcript_on"];

  if (baseTranscript && !baseCompleted && finalizedReassessments < 1) {
    return 1;
  }

  for (let i = 1; i < 50; i++) {
    const transcript = chat.contextVariables[`local:erReassessment_${i}_transcript`];
    const completedOn =
      chat.contextVariables[`local:erReassessment_${i}_on`] || chat.contextVariables[`local:erReassessment_${i}_transcript_on`];

    if (transcript && !completedOn && i > finalizedReassessments) {
      return i;
    }
  }

  const highestIndex = getHighestReassessmentIndex(chat);

  return Math.max(finalizedReassessments, highestIndex) + 1;
};

export const getNextProcedureIndex = (chat) => {
  if (!chat?.contextVariables) return 1;

  let maxIndex = 0;

  for (let i = 1; i < 50; i++) {
    if (chat.contextVariables[`local:erProcedure_${i}_transcript`]) {
      maxIndex = i;
    }
  }

  return maxIndex + 1;
};

export const useErPhases = (encounterType, chat) => {
  return useMemo(() => getSelectablePhases(getEncounterSections(encounterType), chat), [encounterType, chat]);
};
